'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import ChannelBadge from '@/components/ui/ChannelBadge';
import StatusBadge from '@/components/ui/StatusBadge';
import PriorityBadge from '@/components/ui/PriorityBadge';
import SentimentBadge from '@/components/ui/SentimentBadge';
import { toast } from 'sonner';

const interactionTimeline = [
  { id: 'tl-c360-001', date: '16 Aug 2026', time: '14:32', channel: 'PHONE' as const, topic: 'MySejahtera mobile number update guidance', status: 'Resolved', agency: 'MOH', duration: '06:43', sentiment: 'Neutral' as const, caseId: 'MYGCC-2026-008431' },
  { id: 'tl-c360-002', date: '12 Aug 2026', time: '09:14', channel: 'WHATSAPP' as const, topic: 'Asked about MySejahtera account update procedure', status: 'Resolved', agency: 'MOH', duration: '—', sentiment: 'Neutral' as const, caseId: null },
  { id: 'tl-c360-003', date: '02 Aug 2026', time: '11:45', channel: 'LIVE CHAT' as const, topic: 'Government assistance eligibility — STR enquiry', status: 'Resolved', agency: 'KPKT', duration: '12:20', sentiment: 'Positive' as const, caseId: 'MYGCC-2026-007912' },
  { id: 'tl-c360-004', date: '18 Jul 2026', time: '08:20', channel: 'EMAIL' as const, topic: 'Application follow-up — MySejahtera registration', status: 'Closed', agency: 'MOH', duration: '—', sentiment: 'Neutral' as const, caseId: 'MYGCC-2026-007210' },
  { id: 'tl-c360-005', date: '05 Jul 2026', time: '15:02', channel: 'PHONE' as const, topic: 'Road tax renewal inquiry — JPJ', status: 'Resolved', agency: 'JPJ', duration: '04:15', sentiment: 'Positive' as const, caseId: null },
  { id: 'tl-c360-006', date: '22 Jun 2026', time: '10:30', channel: 'WHATSAPP' as const, topic: 'Passport renewal documentation query', status: 'Resolved', agency: 'Immigration', duration: '—', sentiment: 'Positive' as const, caseId: null },
  { id: 'tl-c360-007', date: '10 Jun 2026', time: '14:05', channel: 'PHONE' as const, topic: 'KWSP withdrawal eligibility', status: 'Closed', agency: 'KWSP', duration: '08:32', sentiment: 'Concerned' as const, caseId: 'MYGCC-2026-006112' },
  { id: 'tl-c360-008', date: '28 May 2026', time: '09:45', channel: 'EMAIL' as const, topic: 'Income tax filing assistance request', status: 'Closed', agency: 'LHDN', duration: '—', sentiment: 'Neutral' as const, caseId: null },
];

const openCases = [
  { id: 'CASE-2026-081245', title: 'MySejahtera Account Update', agency: 'Ministry of Health', status: 'Resolved', priority: 'Medium' as const, created: '12 Aug 2026', updated: '16 Aug 2026' },
  { id: 'CASE-2026-081831', title: 'Health Facility Complaint', agency: 'Ministry of Health', status: 'Awaiting Agency', priority: 'Medium' as const, created: '15 Aug 2026', updated: '15 Aug 2026' },
];

const closedCases = [
  { id: 'CASE-2026-079120', title: 'STR Eligibility Verification', agency: 'KPKT', status: 'Closed', priority: 'Low' as const, created: '02 Aug 2026', updated: '04 Aug 2026' },
  { id: 'CASE-2026-077010', title: 'MySejahtera Registration', agency: 'MOH', status: 'Closed', priority: 'Low' as const, created: '18 Jul 2026', updated: '20 Jul 2026' },
  { id: 'CASE-2026-074231', title: 'KWSP Withdrawal Application', agency: 'KWSP', status: 'Closed', priority: 'Medium' as const, created: '10 Jun 2026', updated: '15 Jun 2026' },
  { id: 'CASE-2026-071024', title: 'Passport Renewal Guidance', agency: 'Immigration', status: 'Closed', priority: 'Low' as const, created: '22 Jun 2026', updated: '23 Jun 2026' },
  { id: 'CASE-2026-068412', title: 'Income Tax Filing Assistance', agency: 'LHDN', status: 'Closed', priority: 'Low' as const, created: '28 May 2026', updated: '31 May 2026' },
];

export default function Customer360Content() {
  const [activeTab, setActiveTab] = useState<'timeline' | 'open' | 'closed' | 'channels'>('timeline');

  return (
    <div className="max-w-screen-2xl mx-auto px-6 py-5">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-700 text-foreground">Customer 360</h1>
          <p className="text-[13px] text-muted-foreground mt-0.5">Complete citizen interaction history across all channels</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-ghost px-3 py-1.5 border border-border flex items-center gap-1.5 text-[13px]">
            <Icon name="MagnifyingGlassIcon" size={14} />
            Search Citizen
          </button>
          <button className="btn-primary px-3 py-1.5 flex items-center gap-1.5 text-[13px]">
            <Icon name="PlusIcon" size={14} />
            New Interaction
          </button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Left: Citizen Profile */}
        <div className="col-span-12 xl:col-span-3 space-y-4">
          {/* Profile Card */}
          <div className="card-elevated p-4">
            <div className="flex flex-col items-center text-center mb-4">
              <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center text-white text-2xl font-700 mb-2">AF</div>
              <div className="text-[16px] font-700 text-foreground">Ahmed Firdaus</div>
              <div className="text-[12px] text-muted-foreground">Existing Citizen Contact</div>
              <div className="flex items-center gap-1.5 mt-1.5">
                <span className="text-[11px] bg-green-50 text-green-600 border border-green-200 px-2 py-0.5 rounded font-600">Verified</span>
                <span className="text-[11px] bg-secondary text-primary px-2 py-0.5 rounded font-500">MyGov ID</span>
              </div>
            </div>

            <div className="space-y-2 text-[12px] border-t border-border pt-3">
              {[
                { icon: 'PhoneIcon', label: 'Phone', value: '+60 12 *** 4581' },
                { icon: 'EnvelopeIcon', label: 'Email', value: 'a.firdaus@***.my' },
                { icon: 'IdentificationIcon', label: 'IC', value: '****-**-4821' },
                { icon: 'MapPinIcon', label: 'Location', value: 'Selangor, Malaysia' },
                { icon: 'LanguageIcon', label: 'Language', value: 'Bahasa Melayu' },
                { icon: 'CalendarIcon', label: 'Registered', value: 'Mar 2024' },
              ].map((f) => (
                <div key={`profile-${f.label}`} className="flex items-center gap-2">
                  <Icon name={f.icon as Parameters<typeof Icon>[0]['name']} size={13} className="text-muted-foreground flex-shrink-0" />
                  <span className="text-muted-foreground w-16 flex-shrink-0">{f.label}</span>
                  <span className="font-500 text-foreground truncate">{f.value}</span>
                </div>
              ))}
            </div>

            <div className="border-t border-border pt-3 mt-3">
              <div className="text-[11px] text-muted-foreground mb-1.5">Preferred Channel</div>
              <ChannelBadge channel="WHATSAPP" />
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: 'Total Interactions', value: '9', icon: 'ChatBubbleLeftRightIcon', color: 'text-primary' },
              { label: 'Open Cases', value: '2', icon: 'FolderOpenIcon', color: 'text-amber-600' },
              { label: 'Closed Cases', value: '5', icon: 'ArchiveBoxIcon', color: 'text-green-600' },
              { label: 'Agencies Contacted', value: '6', icon: 'BuildingOffice2Icon', color: 'text-primary' },
            ].map((s) => (
              <div key={`c360s-${s.label}`} className="card-elevated p-3 text-center">
                <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={16} className={`${s.color} mx-auto mb-1`} />
                <div className={`text-[20px] font-700 font-tabular ${s.color}`}>{s.value}</div>
                <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Agency Engagement */}
          <div className="card-elevated p-4">
            <div className="text-[11px] font-700 uppercase tracking-wide text-muted-foreground mb-3">Agency Engagement</div>
            <div className="space-y-2">
              {[
                { agency: 'MOH', full: 'Kementerian Kesihatan', count: 4, pct: 44 },
                { agency: 'Immigration', full: 'Jabatan Imigresen', count: 2, pct: 22 },
                { agency: 'JPJ', full: 'Jabatan Pengangkutan Jalan', count: 2, pct: 22 },
                { agency: 'KWSP', full: 'KWSP', count: 1, pct: 11 },
                { agency: 'LHDN', full: 'LHDN', count: 1, pct: 11 },
              ].map((a) => (
                <div key={`eng-${a.agency}`} className="space-y-0.5">
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-muted-foreground truncate">{a.full}</span>
                    <span className="font-600 text-foreground ml-2">{a.count}</span>
                  </div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${a.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Main Content */}
        <div className="col-span-12 xl:col-span-9 space-y-4">
          {/* AI Summary */}
          <div className="ai-panel border rounded-md p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="SparklesIcon" size={15} className="text-primary" />
              <h3 className="text-[13px] font-700 text-primary">CX AI Customer Summary</h3>
              <span className="text-[10px] bg-primary text-white px-1.5 py-0.5 rounded font-600">AI</span>
              <span className="text-[10px] text-muted-foreground ml-auto">AI-generated summary · Knowledge-grounded</span>
            </div>
            <p className="text-[13px] text-foreground leading-relaxed">
              Citizen has contacted MyGCC <strong>9 times</strong> across 5 channels within the last 90 days, primarily regarding <strong>healthcare and digital Government services</strong>. Four interactions involved Kementerian Kesihatan Malaysia, primarily for MySejahtera-related guidance. Previous enquiries were resolved without escalation. Citizen communicates in Bahasa Melayu and prefers WhatsApp as their primary channel. No negative sentiment history detected.
            </p>
            <div className="flex items-center gap-3 mt-2.5 pt-2.5 border-t border-ai-border text-[11px] text-muted-foreground">
              <span className="flex items-center gap-1"><Icon name="ShieldCheckIcon" size={11} className="text-green-500" /> Based on verified interaction records</span>
              <span>·</span>
              <span>Last updated: 16 Aug 2026, 14:35</span>
              <span>·</span>
              <span className="text-amber-600 font-600">Human review recommended before sharing with citizen</span>
            </div>
          </div>

          {/* Tabs */}
          <div className="card-elevated overflow-hidden">
            <div className="flex items-center gap-0 border-b border-border px-4 pt-2">
              {[
                { key: 'timeline', label: 'Interaction Timeline', count: interactionTimeline.length },
                { key: 'open', label: 'Open Cases', count: openCases.length },
                { key: 'closed', label: 'Closed Cases', count: closedCases.length },
                { key: 'channels', label: 'Channel History', count: null },
              ].map((tab) => (
                <button
                  key={`c360tab-${tab.key}`}
                  onClick={() => setActiveTab(tab.key as typeof activeTab)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-[12px] font-600 border-b-2 transition-all mr-1
                    ${activeTab === tab.key ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                >
                  {tab.label}
                  {tab.count !== null && (
                    <span className={`text-[10px] rounded-full px-1.5 py-0.5 font-700 ${activeTab === tab.key ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="p-4">
              {/* Timeline */}
              {activeTab === 'timeline' && (
                <div className="space-y-0">
                  {interactionTimeline.map((item, i) => (
                    <div key={item.id} className="flex gap-4 pb-4">
                      <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border-2 ${i === 0 ? 'border-primary bg-primary' : 'border-border bg-white'}`}>
                          <Icon name={item.channel === 'PHONE' ? 'PhoneIcon' : item.channel === 'WHATSAPP' ? 'ChatBubbleOvalLeftEllipsisIcon' : item.channel === 'EMAIL' ? 'EnvelopeIcon' : 'ChatBubbleLeftRightIcon'} size={13} className={i === 0 ? 'text-white' : 'text-muted-foreground'} />
                        </div>
                        {i < interactionTimeline.length - 1 && <div className="w-px flex-1 bg-border mt-1 min-h-[20px]" />}
                      </div>
                      <div className="flex-1 min-w-0 pb-1">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div>
                            <span className="text-[13px] font-700 text-foreground">{item.date}</span>
                            <span className="text-[12px] text-muted-foreground ml-2">{item.time}</span>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <SentimentBadge sentiment={item.sentiment} />
                            <StatusBadge status={item.status} />
                          </div>
                        </div>
                        <div className="text-[13px] text-foreground mb-1.5">{item.topic}</div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <ChannelBadge channel={item.channel} />
                          <span className="text-[11px] text-muted-foreground">{item.agency}</span>
                          {item.duration !== '—' && (
                            <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                              <Icon name="ClockIcon" size={10} /> {item.duration}
                            </span>
                          )}
                          {item.caseId && (
                            <button
                              onClick={() => toast.info(`Opening case ${item.caseId}`)}
                              className="text-[11px] text-primary font-600 hover:underline"
                            >
                              {item.caseId}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Open Cases */}
              {activeTab === 'open' && (
                <div className="space-y-3">
                  {openCases.map((c) => (
                    <div key={c.id} className="border border-border rounded p-3 hover:bg-muted/20 transition-colors">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="text-[12px] font-700 text-primary">{c.id}</div>
                          <div className="text-[14px] font-600 text-foreground mt-0.5">{c.title}</div>
                        </div>
                        <PriorityBadge priority={c.priority} />
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <StatusBadge status={c.status} size="md" />
                        <span className="text-[12px] text-muted-foreground">{c.agency}</span>
                      </div>
                      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                        <span>Created: {c.created}</span>
                        <span>Updated: {c.updated}</span>
                        <div className="flex gap-1.5">
                          <button className="btn-secondary px-2.5 py-1 text-[11px]">Open</button>
                          <button className="btn-ghost px-2.5 py-1 text-[11px] border border-border">Update</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Closed Cases */}
              {activeTab === 'closed' && (
                <div className="overflow-x-auto">
                  <table className="w-full text-[12px]">
                    <thead>
                      <tr className="bg-muted/50 border-b border-border">
                        {['Case ID', 'Title', 'Agency', 'Priority', 'Status', 'Created', 'Updated'].map((h) => (
                          <th key={`ch-${h}`} className="text-left px-3 py-2 text-[11px] font-600 uppercase tracking-wide text-muted-foreground whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {closedCases.map((c) => (
                        <tr key={c.id} className="hover:bg-muted/30 transition-colors">
                          <td className="px-3 py-2.5 font-600 text-primary whitespace-nowrap">{c.id}</td>
                          <td className="px-3 py-2.5 text-foreground max-w-[200px] truncate">{c.title}</td>
                          <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{c.agency}</td>
                          <td className="px-3 py-2.5"><PriorityBadge priority={c.priority} /></td>
                          <td className="px-3 py-2.5"><StatusBadge status={c.status} /></td>
                          <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{c.created}</td>
                          <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{c.updated}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Channel History */}
              {activeTab === 'channels' && (
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                  {[
                    { channel: 'PHONE' as const, count: 3, lastUsed: '16 Aug 2026', resolved: 3 },
                    { channel: 'WHATSAPP' as const, count: 3, lastUsed: '12 Aug 2026', resolved: 3 },
                    { channel: 'LIVE CHAT' as const, count: 1, lastUsed: '02 Aug 2026', resolved: 1 },
                    { channel: 'EMAIL' as const, count: 2, lastUsed: '18 Jul 2026', resolved: 2 },
                  ].map((ch) => (
                    <div key={`chist-${ch.channel}`} className="border border-border rounded p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <ChannelBadge channel={ch.channel} />
                      </div>
                      <div className="text-[24px] font-700 font-tabular text-foreground">{ch.count}</div>
                      <div className="text-[11px] text-muted-foreground">interactions</div>
                      <div className="mt-2 space-y-1 text-[11px]">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Last used</span>
                          <span className="font-500">{ch.lastUsed}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Resolved</span>
                          <span className="font-600 text-green-600">{ch.resolved}/{ch.count}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}